"use client";

import { memo } from "react";
import Link from "next/link";
import { GameImage } from "@/components/shared/game-image/GameImage";
import type { GameSearchResult } from "./types";
import { SearchGamePrice } from "./SearchGamePrice";
import { capture } from "@/lib/analytics/posthog";

interface GameResultItemProps {
  game: GameSearchResult;
  index: number;
  onSelect: () => void;
}

export const GameResultItem = memo(function GameResultItem({
  game,
  index,
  onSelect,
}: GameResultItemProps) {
  const href = `/app/${game.appid}`;

  return (
    <li
      style={{ animationDelay: `${index * 20}ms` }}
      className="animate-slide-in opacity-0"
    >
      <Link
        href={href}
        onClick={() => {
          // `index` is the row's position inside its own sub-list (direct vs
          // "Did you mean"), which is what a reader of the dropdown actually
          // scans — good enough to tell rank 0 from rank 9.
          capture("search result clicked", {
            kind: "game",
            appid: game.appid,
            position: index,
          });
          onSelect();
        }}
        className="block pr-2.5 pl-0 py-0 sm:pr-3 sm:pl-0 sm:py-0 text-xs sm:text-[13px] text-white hover:bg-[#1f2e3f] hover:text-[#66c0f4] transition-all duration-200 group"
      >
        {/* Desktop: single row */}
        <div className="hidden sm:flex items-center gap-2.5 w-full">
          <div className="w-[96px] h-[36px] flex-shrink-0 overflow-hidden">
            <GameImage
              src={game.image}
              alt={game.name}
              width={96}
              height={36}
              className="object-cover !rounded-none !border-0"
            />
          </div>
          <div className="flex-1 min-w-0">
            <span className="group-hover:translate-x-1 inline-block transition-transform duration-200 font-medium truncate max-w-full block">
              {game.name}
            </span>
          </div>
          <div className="flex-shrink-0 flex items-center gap-1">
            <SearchGamePrice
              isFree={game.isFree}
              priceDiscount={game.priceDiscount}
              priceInitialFormatted={game.priceInitialFormatted}
              priceFinalFormatted={game.priceFinalFormatted}
              priceFinal={game.priceFinal}
            />
          </div>
        </div>
        {/* Mobile: image + name/price stacked */}
        <div className="flex sm:hidden items-center gap-2 w-full">
          <div className="w-[56px] h-[21px] flex-shrink-0 overflow-hidden">
            <GameImage
              src={game.image}
              alt={game.name}
              width={56}
              height={21}
              className="object-cover !rounded-none !border-0"
            />
          </div>
          <div className="flex-1 min-w-0">
            <span className="font-medium truncate block text-[11px] leading-tight">
              {game.name}
            </span>
            <div className="flex items-center gap-1 mt-0.5">
              <SearchGamePrice
                isFree={game.isFree}
                priceDiscount={game.priceDiscount}
                priceInitialFormatted={game.priceInitialFormatted}
                priceFinalFormatted={game.priceFinalFormatted}
                priceFinal={game.priceFinal}
              />
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
});
